import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, CloudFrontDistribution, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'distributions' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'requests', label: 'Requests' },
  { key: 'e5xx',     label: '5xx %'    },
  { key: 'e4xx',     label: '4xx %'    },
];

const DEFAULT_SORT = 'domain-asc';
const FACET_KEYS   = ['enabled', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'domain-asc',      label: 'Domain A → Z'    },
  { value: 'domain-desc',     label: 'Domain Z → A'    },
  { value: 'monitored-first', label: 'Monitored first' },
];

const FACETS: FacetDef[] = [
  {
    key: 'enabled',
    label: 'State',
    options: [
      { value: 'enabled',  label: 'Enabled'  },
      { value: 'disabled', label: 'Disabled' },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * CloudFront monitoring is at the distribution level (CloudWatch DistributionId
 * dimension, us-east-1) — 5xx and 4xx error rates. The monitored resource
 * identifier is the distribution id. Per-cache-behavior breakdown is not
 * supported in this version.
 */
@Component({
  selector: 'app-cloudfront-detail',
  templateUrl: './cloudfront-detail.component.html',
})
export class CloudFrontDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  distributions: CloudFrontDistribution[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview',      label: 'Overview'      },
    { id: 'distributions', label: 'Distributions' },
    { id: 'activity',      label: 'Activity'      },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'cloudfront', name: 'CloudFront', icon: 'cloud', route: '/cloud/cloudfront', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('cloudfront').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the distribution list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getCloudFrontDistributions(bypass).subscribe(distributions => {
      this.distributions = distributions;
      this.loading       = false;
      this.refreshing    = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'CLOUDFRONT'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'distributions';
    this.selectResource(item.resourceId);
  }

  // ── Overview counts (standardized: total + monitored, then service-specific) ──

  get totalCount():     number { return this.distributions.length; }
  get monitoredCount(): number { return this.distributions.filter(d => this.isMonitored(d.id)).length; }
  get enabledCount():   number { return this.distributions.filter(d => d.enabled).length; }
  get deployedCount():  number { return this.distributions.filter(d => d.status === 'Deployed').length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredDistributions(): CloudFrontDistribution[] {
    let result = this.distributions;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(item => item.domainName.toLowerCase().includes(q) || item.id.toLowerCase().includes(q));

    const enabled = this.filters.facets['enabled'] ?? [];
    if (enabled.length) {
      result = result.filter(item =>
        (enabled.includes('enabled')  && item.enabled) ||
        (enabled.includes('disabled') && !item.enabled));
    }

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(item => {
        const mon = this.isMonitored(item.id);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(distributions: CloudFrontDistribution[], sort: string): CloudFrontDistribution[] {
    return [...distributions].sort((a, b) => {
      switch (sort) {
        case 'domain-desc':     return b.domainName.localeCompare(a.domainName);
        case 'monitored-first': {
          const aM = this.isMonitored(a.id) ? 0 : 1;
          const bM = this.isMonitored(b.id) ? 0 : 1;
          return aM - bM || a.domainName.localeCompare(b.domainName);
        }
        default: return a.domainName.localeCompare(b.domainName); // domain-asc
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  statusClass(status: string): string {
    return status === 'Deployed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700';
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(distributionId: string): boolean { return this.cloud.isMonitored('cloudfront', distributionId); }

  toggleMonitoring(distributionId: string): void {
    const current = this.cloud.isMonitored('cloudfront', distributionId);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('cloudfront', distributionId, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(distributionId: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: distributionId }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(distributionId: string): void  { this.drawerItemId = distributionId; }
  closeItemSettings(): void                        { this.drawerItemId = null; }

  itemOverrideSummary(distributionId: string): string {
    return this.cloud.getItemOverrideSummary('cloudfront', distributionId);
  }
}
